import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { scanError, useStartScan } from '../components/site.js';
import { Button, Card, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

const active = (scan: Schemas['Scan'] | undefined) =>
  scan?.status === 'queued' || scan?.status === 'running';

/** What went wrong with a link, in words. */
function linkProblem(link: Schemas['CrawlLink'], t: TFunction) {
  if (link.httpStatus !== null) return t('links.status', { status: link.httpStatus });
  return t(`links.errors.${link.errorCode ?? 'unknown'}`, {
    defaultValue: t('links.errors.unknown'),
  });
}

/** Broken links of the latest crawl and a manual re-check. */
export function Links() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'crawl'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'crawl', limit: 5 } },
        }),
      ),
    refetchInterval: (query) => (active(query.state.data?.items[0]) ? 5000 : false),
  });
  const running = scans.data?.items.find(active);
  const latest = scans.data?.items.find((s) => !active(s));
  const links = useQuery({
    queryKey: ['site', siteId, 'links', latest?.id],
    queryFn: () =>
      unwrap(
        api.GET('/scans/{scanId}/links', {
          params: { path: { scanId: latest?.id ?? '' }, query: { broken: 'true', limit: 200 } },
        }),
      ),
    enabled: Boolean(latest),
  });
  const start = useStartScan(siteId, 'crawl');

  return (
    <>
      <PageTitle
        actions={
          <Button busy={start.isPending} disabled={Boolean(running)} onClick={() => start.mutate()}>
            {t('links.recheck')}
          </Button>
        }
      >
        {t('links.title')}
      </PageTitle>
      <div className="mb-4 flex flex-col gap-3">
        {start.isError ? (
          <Notice kind="error">
            {scanError(start.error, t)}{' '}
            {start.error instanceof ApiError && start.error.code === 'FORBIDDEN' ? (
              <Link to={`/sites/${siteId}/settings`} className="underline">
                {t('scan.toSettings')}
              </Link>
            ) : null}
          </Notice>
        ) : null}
        {running ? <Notice kind="info">{t('links.running')}</Notice> : null}
      </div>
      {scans.isPending ? <Loading /> : null}
      {scans.isError ? (
        <LoadError message={errorMessage(scans.error, t)} onRetry={() => void scans.refetch()} />
      ) : null}
      {scans.data && !latest && !running ? <Notice kind="info">{t('links.none')}</Notice> : null}
      {latest ? (
        <Card className="mb-4">
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-sm text-slate-700">{t('links.checkedAt')}</dt>
              <dd className="font-semibold">
                {formatDateTime(latest.finishedAt ?? latest.createdAt, i18n.language, timeZone)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-700">{t('links.pages')}</dt>
              <dd className="font-semibold">{latest.pagesCrawled ?? 0}</dd>
            </div>
            <div>
              <dt className="text-sm text-slate-700">{t('links.links')}</dt>
              <dd className="font-semibold">{latest.linksChecked ?? 0}</dd>
            </div>
          </dl>
          {latest.status !== 'completed' ? (
            <p className="mt-3 text-sm text-slate-700">{t(`links.scanStatus.${latest.status}`)}</p>
          ) : null}
        </Card>
      ) : null}
      {links.isError ? (
        <LoadError message={errorMessage(links.error, t)} onRetry={() => void links.refetch()} />
      ) : null}
      {links.data && links.data.items.length === 0 ? (
        <Notice kind="success">{t('links.noBroken')}</Notice>
      ) : null}
      {links.data && links.data.items.length > 0 ? (
        <section aria-labelledby="broken-title">
          <h2 id="broken-title" className="mb-3 text-xl font-semibold">
            {t('links.broken', { count: links.data.total })}
          </h2>
          <ul className="flex flex-col gap-2">
            {links.data.items.map((l) => (
              <li key={l.id} className="rounded-md border border-slate-200 bg-white px-4 py-3">
                <p className="break-all font-medium">{l.targetUrl}</p>
                <p className="text-sm">
                  <strong className="text-red-800">{linkProblem(l, t)}</strong>
                  {' · '}
                  {t(`links.kinds.${l.kind}`)}
                  {' · '}
                  {l.isInternal ? t('links.internal') : t('links.external')}
                </p>
                <p className="break-all text-sm text-slate-700">
                  {t('links.foundOn')} {l.sourceUrl}
                  {l.anchorText ? ` ${t('links.anchor', { text: l.anchorText })}` : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
